import "./bootstrap";
import "./vendor/bootstrap";
import { createApp, createSSRApp, h } from "vue";
import { createInertiaApp } from "@inertiajs/vue3";
// app.css (Tailwind entry) is loaded as a <head> stylesheet via the @vite
// directive in app.blade.php, so it isn't imported here — that keeps it a real
// stylesheet (styled on first SSR paint) instead of JS-injected.
import "primeicons/primeicons.css";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { ZiggyVue, route } from "../../vendor/tightenco/ziggy/src/js";
import { Ziggy } from "./ziggy";
import AppSetup from "./init";
import Tooltip from "primevue/tooltip";
import Countdown from "@chenfengyuan/vue-countdown";
import ToastService from 'primevue/toastservice'
import Toast from 'primevue/toast'
import { hydrateIfNeeded } from './plugins/sideload';
import AppImage from './Components/AppImage.vue';
import { initCookieConsent } from './plugins/cookie-consent';
import { initTooltipViewportClamp } from './plugins/tooltip-viewport';

// Adopt the per-request base URL the server injects as `window.Ziggy = { url }`
// in app.blade.php (that inline script runs before this deferred module). The
// `url` baked into ziggy.js is fixed at build time from APP_URL, but the app is
// served on multiple domains (e.g. islandroutes.com and journeys.islandroutes.com);
// without this, absolute route() calls emit whichever single domain was baked in,
// so links jump to the wrong host. This lives here — not in ziggy.js — because
// that file is regenerated by `artisan ziggy:generate` on every build
// (vite.config.js), which would wipe an edit there.
if (window.Ziggy?.url) Ziggy.url = window.Ziggy.url;

// Expose route() globally (replaces @routes() blade directive)
window.Ziggy = Ziggy;
window.route = (name: string, params?: any, absolute: boolean = false) => route(name, params, absolute, Ziggy);

createInertiaApp({
    resolve: (name: string) => {
        // Lazy glob (not `eager`) so each page is its own chunk and only the
        // current route's component is downloaded — keeps the initial bundle
        // (and main-thread parse cost) small. Inertia awaits the returned import.
        const pages = import.meta.glob("./Pages/**/*.vue");
        return pages[`./Pages/${name}.vue`]();
    },
    setup({ el, App, props, plugin }) {
        const boot = async () => {
            // Hydrate side-loaded props from Redis before mounting.
            // Only relevant on the non-SSR fallback path: when SSR is enabled the
            // side-load middleware is bypassed, so there is no `__data_key` and this
            // returns immediately with the full props already present in data-page.
            if (props.initialPage.props.__data_key) {
                props.initialPage.props = await hydrateIfNeeded(props.initialPage.props);
            }

            // Production console policy:
            //  - debug-level notices are hidden entirely (no-op).
            //  - errors are downgraded to warnings: still visible for diagnostics,
            //    but warnings don't count against the Lighthouse best-practices
            //    "errors logged to console" audit.
            // We key off the server's `__isProduction` flag — not Vite's build
            // mode — because the app is served through the Vite dev server even in
            // production, so `import.meta.env.PROD` would be false here.
            if (props.initialPage.props.__isProduction) {
                console.debug = () => {};
                console.error = console.warn.bind(console);
            }

            // If the server rendered page markup (SSR enabled), hydrate it so we
            // reuse the existing DOM instead of throwing it away. Otherwise the
            // mount element is the empty Inertia root, so client-render from scratch.
            const rootProps = { render: () => h(App, props) };
            const vueApp = el.hasChildNodes()
                ? createSSRApp(rootProps)
                : createApp(rootProps);

            vueApp
                .use(plugin)
                .use(ZiggyVue, Ziggy)
                .use(ToastService)
                .use(AppSetup, { props })
                .directive("tooltip", Tooltip)
                .component("countdown", Countdown)
                .component("Toast", Toast)
                .component("AppImage", AppImage)
                .mount(el);

            // Reveal the app by hiding the full-page loader (rendered in
            // app.blade.php) now that the app is mounted. Two rAFs so we hide
            // only after the first post-mount frame has been painted.
            requestAnimationFrame(() =>
                requestAnimationFrame(() => (window as any).hidePageLoader?.())
            );

            initCookieConsent();
            initTooltipViewportClamp();
        };

        boot();
    },
    progress: {},
});
